import type { ReactElement, ReactNode } from 'react';

import { Button, Modal } from './ui';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /** Server refusal of the confirmed action (e.g. 422 invalid_ref) — the
   *  dialog stays open and shows it; without it a refused delete would just
   *  silently do nothing. */
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Destructive/idempotent-action confirmation. A `window.confirm` would block
 * the whole app and is unstyleable; this is the same primitive set as the rest
 * of the shell. The danger button is the primary of this dialog.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Удалить',
  danger = true,
  busy,
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps): ReactElement {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="confirm-body">
        {description}
        {/* Not a badge: server refusals are long sentences («team … still has
            4 people — move them to another team first») and `.badge` is
            `white-space: nowrap` — inside a 720px modal body the tail got
            clipped. `.field__error` is the wrapping danger text of form
            fields, already themed for both themes. */}
        {error ? (
          <p className="field__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <div className="confirm-body__actions">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
          Отмена
        </Button>
        <Button variant={danger ? 'danger' : 'primary'} size="sm" onClick={onConfirm} loading={busy}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
